import { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Eye, EyeOff } from 'lucide-react';
import { signin } from '../../utils/queries';
import { MyContext } from '../../context';
import AuthCard, { FormError } from './AuthCard';

const LoginSchema = Yup.object().shape({
	email: Yup.string().trim().email('Enter a valid email address.').required('Email is required.'),
	password: Yup.string().required('Password is required.'),
});

// /signin answers { error: "<message>" } for bad credentials (ResponseBuilder),
// but its rate limiter answers { error: { message } } (ApiResponse).
const signinErrorMessage = (result) => {
	const error = result?.error;
	if (typeof error === 'string') return error;
	return error?.message || 'Login failed. Please check your credentials.';
};

export default function LoginForm() {
	const { updateUserProfile } = useContext(MyContext);
	const [showPassword, setShowPassword] = useState(false);

	const handleSubmit = async (values, { setSubmitting, setStatus }) => {
		setStatus(undefined);

		const result = await signin(values.email.trim(), values.password);
		setSubmitting(false);

		if (!result?.session?.token) {
			setStatus(signinErrorMessage(result));
			return;
		}

		localStorage.setItem('authToken', result.session.token);
		updateUserProfile({
			email: result.session.email || '',
			firstName: result.session.firstname || '',
			lastName: result.session.lastname || '',
			fullName: ((result.session.firstname || '') + ' ' + (result.session.lastname || '')).trim(),
			id: result.session.userId || '',
			role: result.session.role || '',
		});

		window.location.href = '/';
	};

	return (
		<AuthCard title='Welcome back' subtitle='Sign in to PI eCommerce Admin'>
			<Formik initialValues={{ email: '', password: '' }} validationSchema={LoginSchema} onSubmit={handleSubmit}>
				{({ isSubmitting, status }) => (
					<Form className='space-y-4'>
						<FormError message={status} />

						<div className='space-y-2'>
							<label htmlFor='email' className='text-sm font-medium text-gray-700'>
								Email address
							</label>
							<Field
								id='email'
								name='email'
								type='email'
								placeholder='Enter your email'
								autoComplete='email'
								className='form-input'
							/>
							<ErrorMessage name='email' component='p' className='error' />
						</div>

						<div className='space-y-2'>
							<label htmlFor='password' className='text-sm font-medium text-gray-700'>
								Password
							</label>
							<div className='relative'>
								<Field
									id='password'
									name='password'
									type={showPassword ? 'text' : 'password'}
									placeholder='••••••••'
									autoComplete='current-password'
									className='form-input pr-10'
								/>
								<button
									type='button'
									className='absolute inset-y-0 right-0 pr-3 flex items-center'
									onClick={() => setShowPassword(!showPassword)}
									aria-label={showPassword ? 'Hide password' : 'Show password'}
								>
									{showPassword ? (
										<EyeOff className='h-4 w-4 text-gray-400' />
									) : (
										<Eye className='h-4 w-4 text-gray-400' />
									)}
								</button>
							</div>
							<ErrorMessage name='password' component='p' className='error' />
						</div>

						<div className='flex items-center justify-end'>
							<Link
								to='/forgot-password'
								className='text-xs text-gray-400 hover:text-pi-red/90 hover:underline'
							>
								Forgot password?
							</Link>
						</div>

						<button
							type='submit'
							disabled={isSubmitting}
							className='btn-primary disabled:opacity-50 disabled:cursor-not-allowed'
						>
							{isSubmitting ? 'Signing In...' : 'Sign In'}
						</button>
					</Form>
				)}
			</Formik>

			<p className='text-center text-sm text-gray-600'>
				Don't have an account?{' '}
				<Link to='/register' className='text-pi-red hover:underline'>
					Create account
				</Link>
			</p>
		</AuthCard>
	);
}
